<template>
    <v-card
        class="mx-auto resident-card mb-4"
        :class="{'resident-overdue': request.overdue}"
        max-width="344"
        elevation="6"
    >
        <v-card-title class="resident-title black--text py-2 px-4 breakWord">
            {{ fullName || 'תושב ללא שם' }}
        </v-card-title>

        <v-card-subtitle class="px-4 pb-0 black--text">
            <div>{{ displayAddress }}</div>
            <div class="mt-1">
                <b>תאריך יעד:</b> {{ formattedDate }}
                <v-chip v-if="request.overdue" x-small color="error" class="ms-1" label>באיחור</v-chip>
            </div>
            <div v-if="request.requestNumber"><b>מס' קריאה:</b> {{ request.requestNumber }}</div>
            <div v-if="request.phone"><b>טלפון:</b> <span class="phone-number">{{ request.phone }}</span></div>
            <div v-if="request.electronic_device"><b>מוצר לפינוי:</b> {{ request.electronic_device }}</div>
            <div><b>אופן פינוי:</b> {{ pickupLocation }}</div>
            <div v-if="request.free_ship" class="mt-1">
                <v-chip x-small color="deep-purple" text-color="white" label>תושב נכה/ותיק</v-chip>
            </div>
        </v-card-subtitle>

        <v-card-text class="px-4 pt-3">
            <!-- contact actions -->
            <v-row class="mb-1" no-gutters justify="space-between">
                <v-btn color="blue" outlined fab x-small class="me-2"
                       :disabled="!request.phone"
                       @click.prevent="callPhone">
                    <v-icon size="18">fas fa-phone</v-icon>
                </v-btn>
                <v-btn color="#25D366" outlined fab x-small class="me-2"
                       :disabled="!request.phone"
                       @click.prevent="openWhatsapp">
                    <v-icon size="18">fab fa-whatsapp</v-icon>
                </v-btn>
                <v-btn color="#79d1e9" outlined fab x-small
                       @click.prevent="openWaze">
                    <v-icon size="18">fab fa-waze</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text small color="primary" @click="expanded = !expanded">
                    טיפול
                    <v-icon class="expand-arrow">{{ expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
                </v-btn>
            </v-row>

            <v-expand-transition>
                <div v-if="expanded" class="mt-2">
                    <!-- הערות לקבלן — מוצג רק בקריאה (בטיפול), לא בתצוגת הכרטיס. MAYM-1863 -->
                    <div v-if="request.note" class="contractor-note mb-3">
                        <b>הערות לקבלן:</b> {{ request.note }}
                    </div>

                    <v-radio-group v-model="status" :mandatory="false" dense hide-details class="mt-0">
                        <v-radio label="הפינוי בוצע" value="בוצע"></v-radio>
                        <v-radio label="הפינוי בוטל" value="בוטל"></v-radio>
                    </v-radio-group>

                    <v-textarea
                        outlined dense rows="2" no-resize hide-details
                        class="mt-3"
                        label="הערות"
                        v-model="note"
                    ></v-textarea>

                    <!-- תעודת נכה/תושב ותיק — רק כאשר התושב נכה/ותיק, לפני צילום התמונה. MAYM-1863 -->
                    <div v-if="request.free_ship" class="mt-3">
                        <v-file-input
                            :ref="'certImage-' + request.id"
                            @change="handleCertificate"
                            accept="image/*"
                            capture="camera"
                            dense
                            hide-details
                            :prepend-icon="'fas fa-id-card'"
                            label="העלאת תעודת נכה/תושב ותיק"
                        ></v-file-input>
                        <v-img v-if="certificateImageUrl" :src="certificateImageUrl" max-width="220" class="mt-2"></v-img>
                    </div>

                    <!-- photo (required only when marking done) -->
                    <div class="mt-3">
                        <v-file-input
                            :ref="'residentImage-' + request.id"
                            @change="handleImage"
                            accept="image/*"
                            capture="camera"
                            dense
                            hide-details
                            :prepend-icon="'fas fa-camera-retro'"
                            :label="status === 'בוצע' ? 'צילום תמונה (חובה)' : 'צילום תמונה'"
                        ></v-file-input>
                        <v-img v-if="imageUrl" :src="imageUrl" max-width="220" class="mt-2"></v-img>
                    </div>

                    <v-btn color="primary" block class="mt-3" :depressed="true"
                           :disabled="!status"
                           @click.prevent="submit">
                        שלח
                    </v-btn>
                </div>
            </v-expand-transition>
        </v-card-text>

        <!-- An admin is acting on a contractor's record, and CRM will record
             that contractor as the actor — say so before it happens. MAYM-1877. -->
        <v-dialog v-model="confirmDialog" max-width="330" persistent>
            <v-card>
                <v-card-title class="confirm-title">אישור פעולה</v-card-title>
                <v-card-text class="confirm-text">
                    הפעולה תתבצע בשם <strong>{{ request.ownerName }}</strong> ותשפיע על הנתונים שלו.
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="confirmDialog = false">ביטול</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed @click="confirmSubmit">אישור</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-overlay :value="innerOverlay" class="overlayLoader" absolute>
            <v-progress-circular indeterminate size="48"></v-progress-circular>
        </v-overlay>
        <v-snackbar v-model="snackbar" :timeout="4000" top :color="snackBarColor" class="text-center">
            {{ snackBarText }}
        </v-snackbar>
    </v-card>
</template>

<script>
import moment from 'moment'

export default {
    name: "vResidentRequestCard",
    props: ['request'],
    data() {
        return {
            expanded: false,
            status: '',
            note: '',
            imageUrl: '',
            imageUploaded: false,
            certificateImageUrl: '',
            innerOverlay: false,
            snackbar: false,
            snackBarColor: '',
            snackBarText: '',
            confirmDialog: false,
        }
    },
    computed: {
        /* an admin's list spans several contractors, so acting on a request
           needs confirming — see submit() */
        isAdmin() {
            return this.$store.state.isAdmin;
        },
        fullName() {
            return [this.request.firstName, this.request.lastName].filter(Boolean).join(' ').trim();
        },
        displayAddress() {
            const r = this.request;
            if (r.address || r.city) {
                return [r.address, r.houseNumber].filter(Boolean).join(' ') + (r.city ? ', ' + r.city : '');
            }
            return r.address_long || 'כתובת לא זמינה';
        },
        formattedDate() {
            return this.request.dueDate ? moment(this.request.dueDate).format('DD/MM/YYYY') : '—';
        },
        // מהבית כאשר הפינוי בתשלום (pay) או חינם לנכה/ותיק (free_ship), אחרת ממדרכה. MAYM-1863
        pickupLocation() {
            return (this.request.pay || this.request.free_ship) ? 'מהבית' : 'ממדרכה';
        },
    },
    methods: {
        /* admins confirm first; contractors submit straight away */
        submit() {
            if (this.isAdmin && this.request.ownerName) {
                this.confirmDialog = true;
                return;
            }
            this.doSubmit();
        },
        confirmSubmit() {
            this.confirmDialog = false;
            this.doSubmit();
        },
        callPhone() {
            if (this.request.phone) window.open('tel:' + this.request.phone);
        },
        openWhatsapp() {
            if (!this.request.phone) return;
            const phone = this.request.phone.replace('0', '972');
            window.open('https://api.whatsapp.com/send?phone=' + phone);
        },
        openWaze() {
            const r = this.request;
            const q = [r.houseNumber, r.address, r.city].filter(Boolean).join(' ');
            let url = 'https://waze.com/ul?navigate=yes';
            if (r.Position_lat && r.Position_lng) {
                url += '&ll=' + r.Position_lat + ',' + r.Position_lng;
            }
            if (q) url += '&q=' + encodeURIComponent(q);
            window.open(url);
        },
        doSubmit() {
            if (!this.status) return;
            if (this.status === 'בוצע' && !this.imageUrl) {
                this.showSnack('יש לצלם תמונה לפני סימון "בוצע"', 'error');
                return;
            }
            const data = {
                id: this.request.id,
                status: this.status,
                note: this.note,
                image: this.imageUrl,
                certificateImage: this.certificateImageUrl,
                lat: this.request.Position_lat,
                lng: this.request.Position_lng,
                address: this.displayAddress,
                fullname: this.fullName,
            };
            this.innerOverlay = true;
            this.$store.dispatch('SUBMIT_SUB_TASK', data).then(() => {
                this.innerOverlay = false;
                this.showSnack('הדיווח נשמר בהצלחה', 'success');
                this.$emit('submitted', this.request.id);
            }).catch(() => {
                this.innerOverlay = false;
                this.showSnack('שגיאה בשמירת הדיווח, יש לנסות שנית', 'error');
            });
        },
        showSnack(text, color) {
            this.snackBarText = text;
            this.snackBarColor = color;
            this.snackbar = true;
        },
        handleImage(file) {
            if (!file) {
                this.imageUrl = '';
                this.imageUploaded = false;
                return;
            }
            this.resizeToDataUrl(file, (url) => {
                this.imageUrl = url;
                this.imageUploaded = true;
            });
        },
        handleCertificate(file) {
            if (!file) {
                this.certificateImageUrl = '';
                return;
            }
            this.resizeToDataUrl(file, (url) => {
                this.certificateImageUrl = url;
            });
        },
        /* read an image file, resize to max 600px, return a jpeg data-url via cb */
        resizeToDataUrl(file, cb) {
            const reader = new FileReader();
            reader.onload = function (readerEvent) {
                const image = new Image();
                image.onload = function () {
                    const canvas = document.createElement('canvas');
                    const maxSize = 600;
                    let width = image.width;
                    let height = image.height;
                    if (width > height) {
                        if (width > maxSize) { height *= maxSize / width; width = maxSize; }
                    } else {
                        if (height > maxSize) { width *= maxSize / height; height = maxSize; }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(image, 0, 0, width, height);
                    cb(canvas.toDataURL('image/jpeg', 0.6));
                };
                image.src = readerEvent.target.result;
            };
            reader.readAsDataURL(file);
        },
    }
}
</script>

<style scoped lang="scss">
@import '../styles/variables.scss';

/* The number is latin digits inside RTL text: without an explicit direction a
   leading 0 and any dash get reordered on display. */
.phone-number {
    direction: ltr;
    unicode-bidi: embed;
    display: inline-block;
}

.resident-card {
    border-radius: 7px;
    overflow: hidden;

    &.resident-overdue {
        border: 2px solid $error_color;
    }
}

.resident-title {
    font-size: 16px;
    line-height: 22px;
    font-weight: bold;

    &.breakWord {
        word-break: break-word;
    }
}

.expand-arrow {
    margin-right: 10px;
}

.contractor-note {
    font-size: 14px;
    line-height: 20px;
    background: #f5f5f5;
    border-radius: 6px;
    padding: 8px 10px;
    word-break: break-word;
}
</style>
